import { type Component, createEffect, createSignal, Show } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { formatTime } from '@/utils/formatters';
import { useTranslation } from '@/i18n';

const GameTimer: Component = () => {
  const { t } = useTranslation();
  const [urgencyLevel, setUrgencyLevel] = createSignal<'normal' | 'warning' | 'critical' | 'waiting' | 'ended'>('waiting');

  // Check game status based on architecture
  const isGameActiveForArch = () => {
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.session) {
      return gameStore.gameState.session.status === 'active';
    }
    return gameStore.gameState.game?.status === 'active';
  };

  const isGameFinishedForArch = () => {
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.session) {
      return gameStore.gameState.session.status === 'finished';
    }
    return gameStore.gameState.game?.status === 'finished';
  };

  createEffect(() => {
    if (!isGameActiveForArch()) {
      if (isGameFinishedForArch()) {
        setUrgencyLevel('ended');
      } else {
        setUrgencyLevel('waiting');
      }
      return;
    }

    const remaining = gameStore.timeRemaining();

    if (remaining <= 0) {
      setUrgencyLevel('ended');
    } else if (remaining <= 300) { // 5 minutes
      setUrgencyLevel('critical');
    } else if (remaining <= 900) { // 15 minutes
      setUrgencyLevel('warning');
    } else {
      setUrgencyLevel('normal');
    }
  });

  const getTimerClass = () => {
    const level = urgencyLevel();
    const baseClass = 'flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300';

    switch (level) {
      case 'critical':
        return `${baseClass} bg-red-500/10 border border-red-500/30`;
      case 'warning':
        return `${baseClass} bg-amber-500/10 border border-amber-500/30`;
      case 'waiting':
        return `${baseClass} bg-white/5 border border-white/10`;
      case 'ended':
        return `${baseClass} bg-white/5 border border-white/10`;
      default:
        return `${baseClass} bg-emerald-500/10 border border-emerald-500/30`;
    }
  };

  const getTimerTextClass = () => {
    const level = urgencyLevel();
    const baseClass = 'text-2xl font-mono font-bold tracking-wide';

    switch (level) {
      case 'critical':
        return `${baseClass} text-red-400`;
      case 'warning':
        return `${baseClass} text-amber-400`;
      case 'waiting':
        return `${baseClass} text-white/40`;
      case 'ended':
        return `${baseClass} text-white/50`;
      default:
        return `${baseClass} text-emerald-400`;
    }
  };

  const getStatusText = () => {
    const level = urgencyLevel();

    switch (level) {
      case 'critical':
        return `🚨 ${t('timer.timeAlmostUp')}`;
      case 'warning':
        return `⏰ ${t('timer.timeRunningLow')}`;
      case 'waiting':
        return `⏳ ${t('dashboard.waitingForStart')}`;
      case 'ended':
        return `🏁 ${t('timer.gameEnded')}`;
      default:
        return `⏱️ ${t('timer.timeRemaining')}`;
    }
  };

  const isPulse = () => urgencyLevel() === 'critical';

  const formatTimeDisplay = () => {
    const level = urgencyLevel();
    if (level === 'waiting') return '--:--:--';
    if (level === 'ended') return '00:00:00';
    return formatTime(gameStore.timeRemaining());
  };

  return (
    <div class={getTimerClass()}>
      {/* Left side: status */}
      <div class="flex items-center gap-2 text-white/70 text-sm">
        <span>{getStatusText()}</span>
      </div>

      {/* Right side: time */}
      <div
        class={`${getTimerTextClass()} ${isPulse() ? 'animate-timer-pulse' : ''}`}
      >
        {formatTimeDisplay()}
      </div>

      <style>{`
        @keyframes timer-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-timer-pulse {
          animation: timer-pulse 1s infinite;
        }
      `}</style>
    </div>
  );
};

export default GameTimer;